import React, { useState, useEffect, useRef } from "react";
import dc1 from "../assets/dc1.png";
import dc2 from "../assets/dc2.jpg";
import dc3 from "../assets/dc3.png";
import dc4 from "../assets/dc4.jpg";
import dc5 from "../assets/dc5.jpg";
import dc6 from "../assets/dc6.jpeg";
import dc7 from "../assets/dc7.jpeg";
import dc8 from "../assets/dc8.jpeg";

const galleryImages = [
  { src: dc1, alt: "Students learning with projector in micro classroom" },
  { src: dc2, alt: "Edolutions team visiting a classroom" },
  { src: dc3, alt: "Children seated and engaged in learning" },
  { src: dc4, alt: "Team with teachers at a rural school" },
  { src: dc5, alt: "Community classroom session with students" },
  { src: dc6, alt: "Solar powered classroom setup" },
  { src: dc7, alt: "Digital learning in action" },
  { src: dc8, alt: "Girls education initiatives" },
];

const features = [
  {
    icon: "📚",
    title: "100–150 Students",
    desc: "Each micro classroom is designed to serve 100–150 students from nearby villages.",
  },
  {
    icon: "☀️",
    title: "Solar Powered",
    desc: "Runs entirely on solar energy, ensuring continuous learning even in areas with limited electricity.",
  },
  {
    icon: "💻",
    title: "Digital Learning",
    desc: "Classes delivered using LED screens, projectors, laptops, and curated digital content.",
  },
  {
    icon: "👩‍🏫",
    title: "Hybrid Teaching",
    desc: "Lessons taught by local facilitators with support from online teachers broadcasting from Karachi.",
  },
];

const subjects = [
  { icon: "🇬🇧", name: "English" },
  { icon: "🔢", name: "Mathematics" },
  { icon: "🔬", name: "Science" },
  { icon: "📝", name: "Sindhi" },
  { icon: "📖", name: "Urdu" },
  { icon: "🖥️", name: "Digital Skills" },
];

const impactItems = [
  {
    icon: "🎯",
    title: "Reduce Out-of-School Children",
    desc: "Address the 7.8 million out-of-school children in Sindh through accessible digital education.",
  },
  {
    icon: "👧",
    title: "Promote Girls' Education",
    desc: "Empower girls in rural communities with equal access to quality education.",
  },
  {
    icon: "🏡",
    title: "Affordable & Accessible",
    desc: "Provide affordable education in remote villages where traditional schools are unavailable.",
  },
  {
    icon: "🌐",
    title: "Digital Opportunities",
    desc: "Create digital learning pathways for underserved communities across Sindh.",
  },
];

/* ───────── Animated Counter Hook ───────── */
function useCountUp(end, duration = 2000, startCounting = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, startCounting]);
  return count;
}

/* ───────── Stats Counter Component ───────── */
function StatCounter({ value, suffix, label }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const count = useCountUp(value, 2200, visible);
  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl md:text-5xl font-extrabold text-white">
        {count.toLocaleString()}
        {suffix}
      </p>
      <p className="mt-2 text-sm md:text-base text-indigo-100">{label}</p>
    </div>
  );
}

/* ───────── Lightbox Component ───────── */
function Lightbox({ images, index, onClose }) {
  const [curr, setCurr] = useState(index);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurr((p) => (p + 1) % images.length);
      if (e.key === "ArrowLeft")
        setCurr((p) => (p - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurr((p) => (p - 1 + images.length) % images.length);
        }}
        className="absolute left-4 md:left-8 text-white text-4xl hover:scale-125 transition-transform select-none"
      >
        ‹
      </button>
      <img
        src={images[curr].src}
        alt={images[curr].alt}
        className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurr((p) => (p + 1) % images.length);
        }}
        className="absolute right-4 md:right-8 text-white text-4xl hover:scale-125 transition-transform select-none"
      >
        ›
      </button>
      <button
        onClick={onClose}
        className="absolute top-4 right-6 text-white text-3xl hover:scale-110 transition-transform"
      >
        ✕
      </button>
      <div className="absolute bottom-6 flex gap-2">
        {images.map((_, i) => (
          <span
            key={i}
            className={`w-2.5 h-2.5 rounded-full transition-colors cursor-pointer ${
              i === curr ? "bg-white" : "bg-white/40"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setCurr(i);
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════ */
const DigitalClassrooms = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  return (
    <div className="max-sm:max-w-screen mx-auto overflow-hidden">
      {/* ─── HERO ─── */}
      <section className="relative mt-24 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img src={dc5} alt="" className="w-full h-full object-cover" />
        </div>
        {/* Gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#5963F5]/30 via-[#374abe]/30 to-[#1e2a78]/30" />
        {/* decorative circles */}
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-white/5 rounded-full" />

        <div className="relative z-10 text-center py-20 md:py-32 px-6">
          <span className="inline-block bg-white/15 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2 rounded-full mb-6 tracking-wide">
            🌍 Transforming Education in Sindh
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-4xl mx-auto">
            Digital Micro Classrooms
          </h1>
          <p className="mt-6 text-base md:text-lg text-indigo-100 max-w-2xl mx-auto leading-relaxed">
            Bringing quality digital education to{" "}
            <span className="font-bold text-white">7.8 million</span>{" "}
            out-of-school children in rural Sindh through solar-powered,
            technology-enabled classrooms.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="#gallery"
              className="bg-white text-[#5963F5] font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              View Gallery
            </a>
            <a
              href="#impact"
              className="border-2 border-white text-white font-bold px-8 py-3 rounded-lg hover:bg-white/10 transition-colors"
            >
              Our Impact
            </a>
          </div>
        </div>
      </section>

      {/* ─── ABOUT PROGRAM ─── */}
      <section className="py-16 md:py-24 px-6" data-aos="fade-up">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900">
            About the Program
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <p className="mt-8 text-gray-600 leading-relaxed text-base md:text-lg max-w-3xl mx-auto">
            The Edolutions Micro Classrooms Program aims to provide digital
            education to 7.8 million out-of-school children in Sindh, especially
            in rural and underserved areas. The program will establish
            solar-powered digital classrooms in villages, each serving 100–150
            students.
          </p>
          <p className="mt-4 text-gray-600 leading-relaxed text-base md:text-lg max-w-3xl mx-auto">
            Lessons are delivered through digital screens and live or recorded
            classes from the Edolutions teaching studio in Karachi. Students
            study Sindhi, Urdu, English, Mathematics, and Science through a
            structured non-formal education curriculum.
          </p>
        </div>
      </section>

      {/* ─── KEY FEATURES ─── */}
      <section className="py-16 md:py-20 px-6 bg-gray-50" data-aos="fade-up">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900">
            Key Features
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {features.map((f, i) => (
              <div
                key={i}
                data-aos="zoom-in"
                data-aos-delay={i * 100}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all text-center group"
              >
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── IMAGE GALLERY ─── */}
      <section id="gallery" className="py-16 md:py-20 px-6" data-aos="fade-up">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900">
            Our Classrooms in Action
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
            {galleryImages.map((img, i) => (
              <div
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 80}
                className="relative overflow-hidden rounded-xl aspect-[4/3] cursor-pointer group"
                onClick={() => setLightboxIndex(i)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-xs font-medium">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LIGHTBOX ─── */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* ─── CORE SUBJECTS ─── */}
      <section className="py-16 md:py-20 px-6 bg-gray-50" data-aos="fade-up">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900">
            Core Subjects
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mt-12">
            {subjects.map((s, i) => (
              <div
                key={i}
                data-aos="flip-up"
                data-aos-delay={i * 80}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all text-center"
              >
                <div className="text-4xl mb-3">{s.icon}</div>
                <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LEARNING SCHEDULE ─── */}
      <section className="py-16 md:py-20 px-6" data-aos="fade-up">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900">
            Learning Schedule
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {/* Morning */}
            <div
              className="relative rounded-2xl overflow-hidden"
              data-aos="fade-right"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500" />
              <div className="relative p-8 md:p-10 text-white">
                <span className="text-4xl">🌅</span>
                <h3 className="text-xl font-bold mt-4">Morning Shift</h3>
                <p className="mt-3 text-white/90 text-sm leading-relaxed">
                  Non-formal education for out-of-school children ages 6–16,
                  covering core subjects using digital content and live teaching
                  from the Edolutions studio.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-white/90">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Structured curriculum
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Ages 6–16
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Core subjects focus
                  </li>
                </ul>
              </div>
            </div>

            {/* Evening */}
            <div
              className="relative rounded-2xl overflow-hidden"
              data-aos="fade-left"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#5963F5] to-[#374abe]" />
              <div className="relative p-8 md:p-10 text-white">
                <span className="text-4xl">🌙</span>
                <h3 className="text-xl font-bold mt-4">Evening Shift</h3>
                <p className="mt-3 text-white/90 text-sm leading-relaxed">
                  Spoken English classes, digital skills training, and community
                  learning programs for youth and adults.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-white/90">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Spoken English classes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Digital skills training
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    Community learning programs
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section
        id="impact"
        className="relative py-16 md:py-20 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#5963F5] via-[#374abe] to-[#1e2a78]" />
        <div className="relative z-10 max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 px-6">
          <StatCounter value={7800000} suffix="" label="Out-of-School Children in Sindh" />
          <StatCounter value={100} suffix="+" label="Planned Micro Classrooms" />
          <StatCounter value={10000} suffix="+" label="Students to Be Reached" />
          <StatCounter value={6} suffix="" label="Core Subjects Taught" />
        </div>
      </section>

      {/* ─── SOCIAL IMPACT ─── */}
      <section className="py-16 md:py-20 px-6 bg-gray-50" data-aos="fade-up">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900">
            Social Impact
          </h2>
          <div className="w-20 h-1 bg-[#5963F5] mx-auto mt-4 rounded-full" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {impactItems.map((item, i) => (
              <div
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all text-center group"
              >
                <div className="w-16 h-16 rounded-full bg-[#5963F5]/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#5963F5]/20 transition-colors">
                  <span className="text-3xl">{item.icon}</span>
                </div>
                <h3 className="font-bold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VISION ─── */}
      <section className="py-16 md:py-24 px-6" data-aos="fade-up">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-[#5963F5]/10 text-[#5963F5] text-sm font-bold px-5 py-2 rounded-full mb-6">
            🚀 Our Vision
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
            100 Digital Micro Classrooms Across Sindh
          </h2>
          <p className="mt-6 text-gray-600 leading-relaxed text-base md:text-lg">
            Edolutions aims to establish{" "}
            <span className="font-bold text-[#5963F5]">
              100 Digital Micro Classrooms
            </span>{" "}
            across Sindh, reaching{" "}
            <span className="font-bold text-[#5963F5]">10,000+ students</span>{" "}
            and building a scalable model that can expand across Pakistan. This
            sustainable approach combines solar energy, digital content, and
            community involvement to create lasting educational transformation.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg mx-auto">
            <div className="text-center">
              <p className="text-3xl font-extrabold text-[#5963F5]">100</p>
              <p className="text-xs text-gray-500 mt-1">Classrooms</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-extrabold text-[#5963F5]">10K+</p>
              <p className="text-xs text-gray-500 mt-1">Students</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-extrabold text-[#5963F5]">All Sindh</p>
              <p className="text-xs text-gray-500 mt-1">Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5963F5] via-[#374abe] to-[#1e2a78]" />
        <div className="absolute -top-10 -right-10 w-60 h-60 bg-white/5 rounded-full" />
        <div className="absolute bottom-0 left-10 w-40 h-40 bg-white/5 rounded-full" />
        <div className="relative z-10 text-center px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-white">
            Join Us in Transforming Education
          </h2>
          <p className="mt-4 text-indigo-100 max-w-xl mx-auto">
            Together, we can bring education to every child in Sindh. Support the
            Edolutions Digital Micro Classrooms Program and help us build a
            brighter future.
          </p>
          <a
            href="/contactUs"
            className="inline-block mt-8 bg-white text-[#5963F5] font-bold px-10 py-3 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default DigitalClassrooms;
